import React, { useState } from "react";
import { type NextPage, type InferGetStaticPropsType } from "next";
import Link from "next/link";
import { useRouter } from "next/router";
import Image from "next/image";
import Layout from "@components/Layout";
import Pagination, { usePagination } from "@components/Pagination";
import { gePreviousNumbers, getYears } from "lib/db/previousNumbers";

const DEFAULT_ITEMS_PER_PAGE = 24;

const NumeriPrecedenti: NextPage<
  InferGetStaticPropsType<typeof getStaticProps>
> = ({ previousNumbers, yearsArray }) => {
  const router = useRouter();
  const [years, setYears] = useState(
    router.query.year || yearsArray[0].meta_value
  );
  const [number, setNumber] = useState<string>("");
  const previousNumbersShow = usePagination(
    previousNumbers,
    DEFAULT_ITEMS_PER_PAGE
  );

  const min = 269;
  const max = 999;
  function handleChange(event) {
    if (event.target.name === "years") {
      setYears(event.target.value);
      selectNumeriPrecedentiWithYear(event.target.value);
    } else {
      setNumber(event.target.value);
    }
  }

  function selectNumeriPrecedentiWithYear(years): void {
    router.push({
      pathname: `/numeri-precedenti/${years}`,
    });
  }

  function selectNumeriPrecedentiWithNumber(): void {
    router.push({
      pathname: `/n/${number}`,
    });
  }

  function selectNumeriPrecedentiWithNumberPressButton(event): void {
    if (event.code == "Enter") {
      router.push({
        pathname: `/n/${number}`,
      });
    }
  }

  return (
    <>
      <Layout title="Numeri precedenti">
        <div
          className="mx-auto mb-20 grid max-w-box justify-items-end gap-5 px-4 sm:flex sm:flex-row "
          style={{ justifyContent: "flex-end" }}
        >
          <div className="flex flex-row justify-center text-sm sm:text-base">
            <label>
              <span className="hidden min-[320px]:inline">Vai all’</span>
              <span className="max-[320px]:uppercase">anno</span>:
              <select
                name="years"
                value={years}
                onChange={handleChange}
                className="ml-2.5 h-9 w-20 overflow-auto rounded-lg border-[1px] border-solid border-gray p-1 active:border-gray"
              >
                {yearsArray.map((year) => (
                  <option key={year.meta_value} value={year.meta_value}>
                    {year.meta_value}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex flex-row justify-center text-sm sm:text-base">
            <label>
              <span className="hidden min-[320px]:inline">Vai al </span>
              <span className="max-[320px]:uppercase">numero</span>:
              <input
                className="ml-2.5 h-9 w-20 rounded-bl-lg rounded-tl-lg border-[1px] border-solid border-gray p-1 active:border-gray"
                name="number"
                type="number"
                step="any"
                value={number}
                min={min}
                max={max}
                onChange={handleChange}
                onKeyUp={selectNumeriPrecedentiWithNumberPressButton}
              />
            </label>
            <button
              className=" active:lightBlue h-9 rounded-br-lg rounded-tr-lg border-[1px] border-solid border-lightGray bg-lightGray px-2.5 uppercase text-white"
              onClick={() => selectNumeriPrecedentiWithNumber()}
            >
              Vai
            </button>
          </div>
        </div>
        <div className="article mx-auto mt-4 grid max-w-box grid-cols-1 gap-y-11 px-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {previousNumbersShow.map((previousNumber) => (
            <div
              key={previousNumber.name}
              className={`articleWrap numeroPrecedente border-box flex shrink grow basis-[20%] items-stretch border-b border-lightGray px-4 sm:px-8 print:px-0`}
            >
              <article className="flex h-full w-full grow flex-col gap-5 ">
                <Link
                  className="numeri-precedenti-link-number flex h-full grow flex-col uppercase transition-all hover:opacity-80"
                  href={`/n/${previousNumber.slug}`}
                >
                  <div
                    className={`flex w-full flex-col items-center gap-6 bg-lightLightGray shadow-[0_0_30px_#d5cece]`}
                  >
                    {Boolean(previousNumber.img) && (
                      <Image
                        width={295}
                        height={380}
                        src={previousNumber.img}
                        alt=""
                        sizes="(max-width: 639px) 100vw, (max-width: 1023px) 50vw,(max-width: 1279px) 33vw, 25vw"
                        className="h-full w-full object-cover mix-blend-multiply"
                        loading="lazy"
                      />
                    )}
                  </div>
                  <span className="flex items-center justify-between pb-8 pt-5">
                    <span className="numeri-precedenti-span-description text-xl font-bold transition-all">
                      {previousNumber.name.charAt(0) == "0"
                        ? previousNumber.name.substring(1)
                        : previousNumber.name}
                    </span>
                    <span className="numeri-precedenti-span-description flex text-sm font-bold transition-all">
                      {previousNumber.description}
                    </span>
                  </span>
                </Link>
              </article>
            </div>
          ))}
        </div>
        <div
          className={`mx-auto mt-6 max-w-box px-4 text-center ${
            previousNumbers.length > DEFAULT_ITEMS_PER_PAGE ? "" : "hidden"
          }`}
        >
          <Pagination
            totalResults={previousNumbers.length}
            itemsPerPage={DEFAULT_ITEMS_PER_PAGE}
          />
        </div>
      </Layout>
    </>
  );
};

export default NumeriPrecedenti;

export const getStaticProps = async () => {
  const [previousNumbers, yearsArray] = await Promise.all([
    gePreviousNumbers(),
    getYears(),
  ]);

  return {
    props: {
      previousNumbers: previousNumbers,
      yearsArray: yearsArray,
    },
    revalidate: 20 * 60,
  };
};
